import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { of } from 'rxjs';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { Tr } from '@utils';
import { carModelService, FuelType, carListBandService, siteService, FleetVehicle } from '@services';
import { IVehicleQuestionCommonProps } from '@models';
import { useService } from '@hooks';
import { Autocomplete, Grid, MenuItem } from '@mui/material';
import { FormikTextfield } from '@components/common';
import { FormStepLayout, FormStepQuestion } from '@components/portal/common';

const DEFAULT_VERSION_TEXT = 'Standard';
type FormFields = Partial<FleetVehicle>;
const translationPath = (key: string) => `vehicle-list.add-edit-vehicle.question-vehicle-details.${key}`;
const getFormFieldValues = (values?: FormFields): FormFields => ({
	plateNumber: values?.plateNumber || '',
	brand: values?.brand || values?.carModel?.brand || '',
	model: values?.model || values?.carModel?.model || '',
	version: values?.version || '',
	fuelType: (values?.fuelType || values?.carModel?.motorFuelType || '') as FuelType,
	siteId: values?.siteId || '',
	segmentId: values?.segmentId || '',
	parkAreaId: values?.parkAreaId || ''
});

export interface IQuestionVehicleDetailsProps extends IVehicleQuestionCommonProps<FormFields> {}

export const QuestionVehicleDetails: FC<IQuestionVehicleDetailsProps> = ({ values, onBack, onNext, ...props }) => {
	const { t } = useTranslation('portal');
	const [fuelTypes, setFuelTypes] = useState(Object.values(FuelType));
	const [initialValues, setInitialValues] = useState<FormFields>(getFormFieldValues(values));
	useEffect(() => setInitialValues(getFormFieldValues(values)), [values]);

	const formik = useFormik<FormFields>({
		initialValues,
		enableReinitialize: true,
		validateOnChange: true,
		validateOnMount: true,
		validationSchema: yup.object({
			plateNumber: yup.string().required(t('field-required')),
			brand: yup.string().required(t('field-required')),
			model: yup.string().required(t('field-required')),
			fuelType: yup.string().required(t('field-required')),
			siteId: yup.string().required(t('field-required')),
			segmentId: yup.string().required(t('field-required')),
			parkAreaId: yup.string().required(t('field-required'))
		}),
		onSubmit: (formValues, api) => {
			onNext?.(formValues);
			api.setSubmitting(false);
		}
	});

	const { submitForm, handleSubmit } = formik;
	const disabled = !formik.isValid || formik.isSubmitting;

	const { data: sites } = useService(() => siteService.getAll(), []);
	const { data: carListBands } = useService(() => carListBandService.getAll(), []);
	const { data: brands = [] } = useService(() => carModelService.getAllBrands(), []);
	const { data: models = [] } = useService(
		() => (formik.values.brand ? carModelService.getAllModels(formik.values.brand) : of([])),
		[formik.values.brand]
	);
	const { data: parkAreas = [] } = useService(
		() => (formik.values.siteId ? siteService.parkArea(formik.values.siteId).getAll() : of([])),
		[formik.values.siteId]
	);
	const { data: versions = [] } = useService(
		() =>
			formik.values.brand && formik.values.model ? carModelService.getAllVersions(formik.values.brand, formik.values.model) : of([]),
		[formik.values.model]
	);

	useEffect(() => {
		let displayFuelTypes = Object.values(FuelType);

		if (formik.values.version) {
			const searchingVersion = formik.values.version === DEFAULT_VERSION_TEXT ? null : formik.values.version;
			const model = versions.find(({ version }) => version === searchingVersion);
			model && (displayFuelTypes = [model.motorFuelType]);
		} else if (versions.length) {
			displayFuelTypes = versions.map(({ motorFuelType }) => motorFuelType);
		} else if (models.length) {
			displayFuelTypes = models.map(({ motorFuelType }) => motorFuelType);
		}

		setFuelTypes([...new Set(displayFuelTypes)]);
	}, [models, versions, formik.values.version]);

	return (
		<FormStepLayout
			title={<Tr.Portal path={translationPath('title')} />}
			primaryButtonProps={{ disabled, onClick: submitForm, 'data-testid': 'add-edit-vehicle-next-button' }}
			secondaryButtonProps={{ onClick: onBack }}
			hideSecondaryButton
			{...props}
		>
			<FormStepQuestion title={<Tr.Portal path={translationPath('first-question')} />}>
				<form onSubmit={handleSubmit}>
					<Grid container rowSpacing={5} columnSpacing={4}>
						<Grid item xs={6}>
							<FormikTextfield
								formikApi={formik}
								name="plateNumber"
								title={t(translationPath('plate-number'))}
								placeholder={t(translationPath('placeholder.plate-number'))}
								required
							/>
						</Grid>
						<Grid item xs={6}>
							<Autocomplete
								isOptionEqualToValue={(option, value) => option.brand === value.brand}
								getOptionLabel={(option) => (typeof option === 'string' ? option : option.brand)}
								options={Array.isArray(brands) ? brands : []}
								freeSolo
								fullWidth
								onChange={(e, value) => {
									formik.setFieldValue('brand', (typeof value === 'string' ? value : value?.brand) || '');
									formik.setFieldValue('model', '');
									formik.setFieldValue('version', '');
									formik.setFieldValue('fuelType', '');
								}}
								onInputChange={(e, value, reason) => {
									if (reason === 'clear') {
										formik.setFieldValue('brand', '');
										formik.setFieldValue('model', '');
										formik.setFieldValue('version', '');
										formik.setFieldValue('fuelType', '');
									}
								}}
								inputValue={formik.values.brand}
								renderInput={(params) => (
									<FormikTextfield
										{...params}
										formikApi={formik}
										name="brand"
										title={t(translationPath('brand'))}
										placeholder={t(translationPath('placeholder.brand'))}
										required
										InputProps={{
											...params.InputProps,
											endAdornment: params.InputProps.endAdornment
										}}
									/>
								)}
							/>
						</Grid>
						<Grid item xs={6}>
							<Autocomplete
								isOptionEqualToValue={(option, value) => option.model === value.model}
								getOptionLabel={(option) => (typeof option === 'string' ? option : option.model)}
								options={Array.isArray(models) ? models : []}
								freeSolo
								fullWidth
								onChange={(e, value) => {
									formik.setFieldValue('model', typeof value === 'string' ? value : value?.model || '');
									formik.setFieldValue('version', '');
									formik.setFieldValue('fuelType', '');
								}}
								onInputChange={(e, value, reason) => {
									if (reason === 'clear') {
										formik.setFieldValue('model', '');
										formik.setFieldValue('version', '');
										formik.setFieldValue('fuelType', '');
									}
								}}
								inputValue={formik.values.model}
								renderInput={(params) => (
									<FormikTextfield
										{...params}
										formikApi={formik}
										name="model"
										title={t(translationPath('model'))}
										placeholder={t(translationPath('placeholder.model'))}
										required
										disabled={!formik.values.brand}
										InputProps={{
											...params.InputProps,
											endAdornment: params.InputProps.endAdornment
										}}
									/>
								)}
							/>
						</Grid>
						<Grid item xs={6}>
							<Autocomplete
								isOptionEqualToValue={(option, value) => option.version === value.version}
								getOptionLabel={(option) => (typeof option === 'string' ? option : option.version) || DEFAULT_VERSION_TEXT}
								options={versions}
								freeSolo
								fullWidth
								onChange={(e, value) =>
									formik.setFieldValue(
										'version',
										(typeof value === 'string' ? value : value?.version) || DEFAULT_VERSION_TEXT
									)
								}
								onInputChange={(_e, _value, reason) => {
									reason === 'clear' && formik.setFieldValue('version', '');
								}}
								inputValue={formik.values.version}
								renderInput={(params) => (
									<FormikTextfield
										{...params}
										formikApi={formik}
										name="version"
										title={t(translationPath('version'))}
										placeholder={t(translationPath('placeholder.version'))}
										disabled={!formik.values.model || versions?.length <= 0}
										InputProps={{
											...params.InputProps,
											endAdornment: params.InputProps.endAdornment
										}}
									/>
								)}
							/>
						</Grid>
						<Grid item xs={6}>
							<FormikTextfield
								select
								formikApi={formik}
								name="fuelType"
								title={t(translationPath('fuel-type'))}
								placeholder={t(translationPath('placeholder.fuel-type'))}
								required
							>
								{fuelTypes.map((type) => (
									<MenuItem key={`fuel-type-${type}`} value={type}>
										{type}
									</MenuItem>
								))}
							</FormikTextfield>
						</Grid>
						<Grid item xs={6}>
							<FormikTextfield
								select
								formikApi={formik}
								name="segmentId"
								title={t(translationPath('car-list-band'))}
								placeholder={t(translationPath('placeholder.car-list-band'))}
								required
							>
								{(carListBands || []).map(({ id, label }) => (
									<MenuItem key={`car-list-band-${label}-${id}`} value={id}>
										{label}
									</MenuItem>
								))}
							</FormikTextfield>
						</Grid>
						<Grid item xs={6}>
							<FormikTextfield
								select
								formikApi={formik}
								name="siteId"
								title={t(translationPath('location'))}
								placeholder={t(translationPath('placeholder.location'))}
								required
							>
								{(sites || []).map(({ id, name }) => (
									<MenuItem key={`location-${name}-${id}`} value={id}>
										{name}
									</MenuItem>
								))}
							</FormikTextfield>
						</Grid>
						<Grid item xs={6}>
							<FormikTextfield
								select
								formikApi={formik}
								name="parkAreaId"
								title={t(translationPath('park-area'))}
								placeholder={t(translationPath('placeholder.park-area'))}
								required
								disabled={!formik.values.siteId}
							>
								{(parkAreas || []).map(({ id, name }) => (
									<MenuItem key={`park-area-${name}-${id}`} value={id}>
										{name}
									</MenuItem>
								))}
							</FormikTextfield>
						</Grid>
					</Grid>
				</form>
			</FormStepQuestion>
		</FormStepLayout>
	);
};
